import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';
import pluralize from 'pluralize';

import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import scrollToDomClass from 'common/util/scrollToDomClass';

import { MEDIA_QUERY_SMALL_SCREEN } from 'client/constants/mediaQueries';

import withLocationQuery from 'client/helpers/withLocationQuery';

import branch from 'client/core/branch';
import PartnerActions from 'client/actions/Partner';
import abbreviateNumber from 'client/helpers/abbreviateNumber';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrText from 'client/components/SrText';
import SrLabel from 'client/components/SrLabel/SrLabel';
import SrPartnerStatsLeadsModal from 'client/components/SrPartnerStatsLeadsModal';
import SrPartnerStatsToursModal from 'client/components/SrPartnerStatsToursModal';
import SrDataRetrievalText from 'client/components/SrDataRetrievalText/SrDataRetrievalText';

function getPartnerListingStatsId(props) {
  const pathId = [];

  if (props.listingManagementCompany) {
    pathId.push(`listingManagementCompany:${props.listingManagementCompany}`);
  }

  if (props.listingId) {
    pathId.push(`listingId:${props.listingId}`);
  }

  if (props.apartmentId) {
    pathId.push(`apartmentId:${props.apartmentId}`);
  }

  if (props.sinceDate) {
    pathId.push(`sinceDate:${props.sinceDate}`);
  }

  return pathId.join('_');
}

const StatsContainer = ({ children }) => {
  const isSmallScreen = useMediaQuery({
    query: MEDIA_QUERY_SMALL_SCREEN,
  });

  return (
    <SrBox
      borderRadius={isSmallScreen ? '14px' : '24px'}
      backgroundColor="white"
      padding="0 24px 21px 20px"
      boxShadow="0px 5px 21px rgba(25, 51, 64, 0.05)"
    >
      {children}
    </SrBox>
  );
};

@branch((props) => ({
  stats7Day: [
    'partners',
    'listingStats',
    getPartnerListingStatsId({
      listingManagementCompany: props.listingManagementCompany,
      listingId: props.listingId,
      apartmentId: props.apartmentId,
      sinceDate: momentWithAustinTimezone().subtract(6, 'days').startOf('day').toISOString(),
    }),
  ],
  stats180Day: [
    'partners',
    'listingStats',
    getPartnerListingStatsId({
      listingManagementCompany: props.listingManagementCompany,
      listingId: props.listingId,
      apartmentId: props.apartmentId,
    }),
  ],
  currentUser: ['currentUser'],
}))
@withLocationQuery
class SrPartnerListingStatsV2 extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired, // eslint-disable-line react/no-unused-prop-types
    listingManagementCompany: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    listingId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    apartmentId: PropTypes.number, // eslint-disable-line react/no-unused-prop-types
    stats7Day: PropTypes.object,
    stats180Day: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    isUnit: PropTypes.bool,
    locationQuery: PropTypes.object.isRequired,
    enableViewTexts: PropTypes.bool,
    enableViewTours: PropTypes.bool,
  };

  constructor(props) {
    super();

    this.state = {
      isStatsLeadsModalOpen: false,
      isStatsToursModalOpen: props.locationQuery?.openFailedTours === 'true',
    };
  }

  componentDidMount() {
    this._onRefreshStats();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (getPartnerListingStatsId(this.props) !== getPartnerListingStatsId(nextProps)) {
      this._getListingStats(nextProps);
    }
  }

  _getListingStats = (props) => {
    const {
      dispatch,
      listingManagementCompany,
      listingId,
      apartmentId,
      sinceDate,
      forceRefresh,
    } = props;

    const listingStatsId = getPartnerListingStatsId(props);

    dispatch(PartnerActions.getListingStats, {
      listingStatsId,
      listingManagementCompany,
      listingId,
      apartmentId,
      sinceDate,
      forceRefresh,
    });
  };

  _onRefreshStats = () => {
    const { dispatch, listingManagementCompany, listingId, apartmentId } = this.props;

    this._getListingStats({
      dispatch,
      listingManagementCompany,
      listingId,
      apartmentId,
      sinceDate: momentWithAustinTimezone().subtract(6, 'days').startOf('day').toISOString(),
      forceRefresh: true,
    });
    this._getListingStats({
      dispatch,
      listingManagementCompany,
      listingId,
      apartmentId,
      forceRefresh: true,
    });
  };

  _onClickToggleStatsLeadsModal = () => {
    this.setState({
      isStatsLeadsModalOpen: !this.state.isStatsLeadsModalOpen,
    });
  };

  _onClickToggleStatsToursModal = () => {
    this.setState({
      isStatsToursModalOpen: !this.state.isStatsToursModalOpen,
    });
  };

  render() {
    const {
      currentUser,
      stats7Day,
      stats180Day,
      isUnit,
      listingManagementCompany,
      listingId,
      apartmentId,
      locationQuery,
      scheduledLeadToursCount,
      enableViewTexts,
      enableViewTours,
    } = this.props;
    const { isStatsLeadsModalOpen, isStatsToursModalOpen } = this.state;

    if (!stats7Day || !stats180Day) {
      return <SrDataRetrievalText dataIsLoading />;
    }

    const statsData = [
      {
        label: 'Views',
        weekCount: abbreviateNumber(
          stats7Day.rentlinxViews + stats7Day.srViews + stats7Day.supraShowings,
        ),
        totalCount: abbreviateNumber(
          stats180Day.rentlinxViews + stats180Day.srViews + stats180Day.supraShowings,
        ),
        disabled: isUnit,
      },
      {
        label: 'Leads',
        weekCount: abbreviateNumber(
          stats7Day.rentlinxLeads +
            stats7Day.supraShowings +
            stats7Day.srLeads +
            stats7Day.locatorTourLeads +
            stats7Day.partnerTourRequests,
        ),
        totalCount: abbreviateNumber(
          stats180Day.rentlinxLeads +
            stats180Day.supraShowings +
            stats180Day.srLeads +
            stats180Day.locatorTourLeads +
            stats180Day.partnerTourRequests,
        ),
        onEditClick: enableViewTexts && !isUnit ? this._onClickToggleStatsLeadsModal : null,
        disabled: isUnit,
      },
      {
        label: 'Tours',
        weekCount: abbreviateNumber(stats7Day.supraShowings + stats7Day.toursCompleted),
        totalCount: abbreviateNumber(stats180Day.supraShowings + stats180Day.toursCompleted),
        onEditClick: enableViewTours ? this._onClickToggleStatsToursModal : null,
      },
    ];

    return (
      <StatsContainer>
        {scheduledLeadToursCount > 0 && (
          <SrBox
            padding="20px 0 0"
            cursor="pointer"
            onClick={() => scrollToDomClass('SrPartnerLeadsCard', 90)}
          >
            <SrLabel
              justifyCenter
              color="green"
              backgroundColor="rgba(0, 162, 56, 0.07)"
              fontSize="large"
              label={`${scheduledLeadToursCount} Scheduled ${pluralize(
                'Tour',
                scheduledLeadToursCount,
              )}`}
            />
          </SrBox>
        )}
        <SrFlex justify="between" wrap="wrap">
          {statsData.map((stat) => (
            <SrBox
              key={stat.label}
              marginTop="20px"
              marginRight="10px"
              onClick={stat.onEditClick}
              cursor={(stat.onEditClick && 'pointer') || null}
            >
              <SrBox>
                <SrText
                  font="greycliff"
                  fontSize="16px"
                  color="blueGreycliff"
                  opacity="0.5"
                  lineHeight="30px"
                  weight="800"
                >
                  {stat.label}
                </SrText>
                {stat.onEditClick && (
                  <SrBox display="inlineBlock" marginLeft="10px">
                    <SrText
                      font="greycliff"
                      fontSize="16px"
                      color="blue300"
                      lineHeight="30px"
                      weight="700"
                      className={`stats-view-${stat.label}`}
                    >
                      View
                    </SrText>
                  </SrBox>
                )}
              </SrBox>
              <SrText
                font="greycliff"
                fontSize="21px"
                color="blueGreycliff"
                weight="800"
                display="block"
              >
                {stat.weekCount}
                <SrText font="greycliff" fontSize="13px" color="blueGreycliff" weight="800">
                  &nbsp;in 7d
                </SrText>
              </SrText>
              <SrText
                font="greycliff"
                fontSize="21px"
                color="blueGreycliff"
                lineHeight="30px"
                weight="800"
                opacity="0.5"
                display="block"
              >
                {stat.totalCount}
                <SrText
                  font="greycliff"
                  fontSize="13px"
                  color="blueGreycliff"
                  lineHeight="30px"
                  weight="800"
                >
                  &nbsp;total
                </SrText>
              </SrText>
            </SrBox>
          ))}
        </SrFlex>
        {isStatsLeadsModalOpen && (
          <SrPartnerStatsLeadsModal
            onClose={this._onClickToggleStatsLeadsModal}
            stats={stats180Day}
            listingManagementCompany={listingManagementCompany}
            listingId={listingId}
            apartmentId={apartmentId}
            onRefreshStats={this._onRefreshStats}
          />
        )}
        {isStatsToursModalOpen && (
          <SrPartnerStatsToursModal
            onClose={this._onClickToggleStatsToursModal}
            currentUser={currentUser}
            stats={stats180Day}
            listingManagementCompany={listingManagementCompany}
            listingId={listingId}
            apartmentId={apartmentId}
            onRefreshStats={this._onRefreshStats}
            locationQuery={locationQuery}
          />
        )}
      </StatsContainer>
    );
  }
}

SrPartnerListingStatsV2.displayName = 'SrPartnerListingStatsV2';

export default SrPartnerListingStatsV2;
